import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  MenuItem,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
  InputAdornment,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { BASE_URL } from "./Api/ApiUrls";
import { AuthContext } from "./AuthContext/AuthContext";
import { GOOGLEMAPS_API_KEY } from "./Api/ApiUrls";

const propertyTypes = [
  "Plot/land", "Commercial land/plot", "Rent with duplex building",
  "Duplex house", "Rental building", "Pg-offices", "Flat", "Villa",
  "Commercial building", "Apartment", "Others"
];
const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;

const CustomerInterestForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userId, logout } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    area: "",
    propertyType: "",
    min_budget: "",
    max_budget: "",
    latitude: null,
    longitude: null,
  });

  const [predictions, setPredictions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [hasExistingEnquiry, setHasExistingEnquiry] = useState(false);
  const [loading, setLoading] = useState(true);
  const [placesService, setPlacesService] = useState(null);

  // Budget options from your second image
  const budgetOptions = [
    { label: 'NO MIN', value: '' },
    { label: '5L', value: '500000' },
    { label: '10L', value: '1000000' },
    { label: '20L', value: '2000000' },
    { label: '30L', value: '3000000' },
    { label: '40L', value: '4000000' },
    { label: '50L', value: '5000000' },
    { label: '60L', value: '6000000' },
    { label: '70L', value: '7000000' },
    { label: '80L', value: '8000000' },
    { label: '90L', value: '9000000' },
    { label: '1CR', value: '10000000' },
    { label: '1.25CR', value: '12500000' },
    { label: '1.5CR', value: '15000000' },
    { label: '1.75CR', value: '17500000' },
    { label: '2CR', value: '20000000' },
    { label: '2.5CR', value: '25000000' },
    { label: '3CR', value: '30000000' },
    { label: '3.5CR', value: '35000000' },
    { label: '4CR', value: '40000000' },
    { label: '4.5CR', value: '45000000' },
    { label: '5.5CR', value: '55000000' },
    { label: '6 CR', value: '60000000' },
    { label: '8CR', value: '80000000' },
    { label: '9CR', value: '90000000' },
    { label: '10CR', value: '100000000' },
    { label: '15CR', value: '150000000' },
    { label: '20CR', value: '200000000' },
    { label: '25CR', value: '250000000' },
    { label: '30CR', value: '300000000' },
    { label: '40CR', value: '400000000' },
    { label: '50CR', value: '500000000' },
    { label: '60CR', value: '600000000' },
    { label: '70CR', value: '700000000' },
    { label: '80CR', value: '800000000' },
    { label: '90CR', value: '900000000' },
    { label: '100CR++', value: '1000000000' }
  ];

  // Function to get available max budget options based on selected min budget
  const getAvailableMaxBudgets = () => {
    if (!formData.min_budget) return budgetOptions;
    
    const minIndex = budgetOptions.findIndex(option => option.value === formData.min_budget);
    if (minIndex === -1) return budgetOptions;
    
    // Return only budgets greater than the selected min budget (starting from next index)
    return budgetOptions.slice(minIndex + 1);
  };

  // Reset max budget when min budget changes to a higher value
  useEffect(() => {
    if (formData.min_budget && formData.max_budget) {
      const minValue = parseInt(formData.min_budget);
      const maxValue = parseInt(formData.max_budget);
      
      if (maxValue <= minValue) {
        setFormData(prev => ({ ...prev, max_budget: '' }));
      }
    }
  }, [formData.min_budget, formData.max_budget]);

  // Load Google Maps script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setPlacesService(new window.google.maps.places.PlacesService(document.createElement("div")));
    };
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  // Check for existing enquiries
  useEffect(() => {
    const checkExistingEnquiry = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/Enquiry/`);
        const userEnquiries = response.data.filter(
          enquiry => enquiry.user_id == userId
        );
        console.log("User Enquiries:", userEnquiries);
        
        if (userEnquiries.length === 0) {
          const timer = setTimeout(() => {
            setShowForm(true);
          }, 60000);
          return () => clearTimeout(timer);
        } else {
          setHasExistingEnquiry(true);
        }
      } catch (error) {
        console.error("Error checking existing enquiries:", error);
        const timer = setTimeout(() => {
          setShowForm(true);
        }, 60000);
        return () => clearTimeout(timer);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      checkExistingEnquiry();
    }
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAreaChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setFormData(prev => ({ ...prev, area: value, latitude: null, longitude: null }));

    if (value.length > 2 && window.google && window.google.maps && window.google.maps.places) {
      try {
        const service = new window.google.maps.places.AutocompleteService();
        service.getQueryPredictions({ input: value, componentRestrictions: { country: 'in' } }, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setPredictions(predictions || []);
          } else {
            setPredictions([]);
          }
        });
      } catch (error) {
        console.error("Error with Google Places Autocomplete:", error);
        setPredictions([]);
      }
    } else {
      setPredictions([]);
    }
  };

  const handleSelectSuggestion = (prediction) => {
    setInputValue(prediction.description);
    setPredictions([]);

    placesService?.getDetails({ placeId: prediction.place_id, fields: ['geometry'] }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const location = place.geometry.location;
        setFormData(prev => ({
          ...prev,
          area: prediction.description,
          latitude: location.lat(),
          longitude: location.lng(),
        }));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.latitude || !formData.longitude) {
      alert('Please select a valid area from the dropdown.');
      return;
    }

    // Validate that min budget is not greater than max budget
    if (formData.min_budget && formData.max_budget && parseInt(formData.min_budget) > parseInt(formData.max_budget)) {
      alert('Minimum budget cannot be greater than maximum budget.');
      return;
    }

    const payload = {
      user_id: userId,
      area: formData.area?.trim(),
      property_type: formData.propertyType,
      min_budget: formData.min_budget ? parseInt(formData.min_budget) : null,
      max_budget: formData.max_budget ? parseInt(formData.max_budget) : null,
      lat: formData.latitude,
      long: formData.longitude,
    };

    try {
      await axios.post(`${BASE_URL}/Enquiry/`, payload);
      alert("Enquiry submitted!");
      setShowForm(false);
      setHasExistingEnquiry(true);
    } catch (error) {
      console.error("API Error:", error);
      alert("Submission failed.");
    }
  };

  if (loading || hasExistingEnquiry || !userId) {
    return null;
  }

  return (
    <Modal open={showForm} sx={{ backdropFilter: "blur(3px)" }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "90%" : 450,
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          outline: "none"
        }}
      >
        <Box textAlign="center" mb={3}>
          <HomeIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h5" mt={1} fontWeight="600">Property Preferences</Typography>
          <Typography variant="body2" color="text.secondary">Let us know what you're looking for</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box position="relative">
          <TextField
            fullWidth
            label="Preferred Area"
            value={inputValue}
            onChange={handleAreaChange}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LocationOnIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {predictions.length > 0 && (
            <Paper elevation={3} sx={{ position: "absolute", zIndex: 1300, width: "100%", maxHeight: 200, overflowY: "auto" }}>
              {predictions.map(prediction => (
                <MenuItem key={prediction.place_id} onClick={() => handleSelectSuggestion(prediction)}>
                  {prediction.description}
                </MenuItem>
              ))}
            </Paper>
          )}
        </Box>

        <TextField
          select
          fullWidth
          label="Property Type"
          name="propertyType"
          value={formData.propertyType}
          onChange={handleChange}
          required
          sx={{ mb: 2 }}
        >
          {propertyTypes.map(type => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </TextField>

        {/* Budget Range - Two Separate Dropdowns */}
        <Box display="flex" gap={2} mb={3}>
          <TextField
            select
            fullWidth
            label="Min Budget"
            name="min_budget"
            value={formData.min_budget}
            onChange={handleChange}
          >
            {budgetOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            select
            fullWidth
            label="Max Budget"
            name="max_budget"
            value={formData.max_budget}
            onChange={handleChange}
            disabled={!formData.min_budget} // Disable until min budget is selected
          >
            {getAvailableMaxBudgets().map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box display="flex" justifyContent="space-between">
          <Button variant="outlined" onClick={() => setShowForm(false)} sx={{ width: '45%', py: 1.5, borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" type="submit" sx={{ width: '45%', py: 1.5, borderRadius: 2, fontWeight: 'bold' }}>Submit</Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default CustomerInterestForm;